import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '../../../../slices/authSlice';
import { Loader } from "../../../base/Loader";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { LogOutBtn } from "../../shared/Logout";
import { BACKEND_API_URL } from '../../../../config';
import {
    FaCreditCard,
    FaBuilding,
    FaGlobeAmericas,
    FaInfoCircle,
    FaExternalLinkAlt,
    FaCheckCircle,
    FaExclamationTriangle,
    FaSearch
} from "react-icons/fa";

const BusinessInfoItem = ({ label, content }) => (
    <div className="mb-3">
        <span className="font-semibold text-gray-900">{label}:</span>
        <div className="text-gray-700 mt-1">
            {Array.isArray(content) ? (
                content.map((item, index) => (
                    <div key={index}>{item}</div>
                ))
            ) : (
                content
            )}
        </div>
    </div>
);

export function Step5() {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const navigate = useNavigate();
    
    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.auth);
    const userId = userInfo ? userInfo.user.id : null;
    const token = userInfo ? userInfo.token : null;
    const baseUrl = BACKEND_API_URL;

    const businessInfo = {
        location: "Canada",
        type: "Individual or sole proprietorship",
        jobTitle: "Service Provider",
        industries: ["Personal Services", "Landscaping Services"],
        website: "www.shovelhouse.com",
        productDescription: "Snow shoveling and landscaping services",
        statementDescriptor: "Shovelhouse",
        shortenedDescriptor: "SHOVELHOUS",
        primaryProductType: "Services",
    };

    const handleStripeSetup = async () => {
        setLoading(true);

        try {
            const response = await fetch(`${baseUrl}/oauth/connect/${userId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const { stripeUrl } = await response.json();
                window.location.href = stripeUrl;
            } else {
                console.error('Error initiating Stripe connection:', response.statusText);
            }
        } catch (error) {
            console.error('Error initiating Stripe connection:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check for Stripe callback parameters
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        const error = queryParams.get('error');
        const errorDescription = queryParams.get('error_description');

        if (error) {
            setStatus('failure');
            console.error('Error finalizing Stripe connection:', errorDescription);
        }

        if (code && state) {
            finalizeStripeConnection(code, state);
        }
    }, []);

    const finalizeStripeConnection = async (code, state) => {
        try {
            setLoading(true);
            const response = await fetch(`${baseUrl}/oauth/stripe/callback?code=${code}&state=${state}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                dispatch(setCredentials({ ...data }));
                setStatus('success');
            } else {
                setStatus('failure');
                console.error('Error finalizing Stripe connection:', response.statusText);
            }
        } catch (error) {
            setStatus('failure');
            console.error('Error finalizing Stripe connection:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStripeLoginNavigate = () => {
        window.open('https://dashboard.stripe.com/login', '_blank');
    };

    // Success State - User completed Stripe setup
    if (status === 'success') {
        if (userInfo.user.chargesEnabled) {
            return (
                <div className="flex flex-col px-6 py-10 max-w-4xl mx-auto">
                    <div className="text-center py-8">
                        <FaCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">You've Successfully Signed Up!</h2>
                        <p className="text-gray-600 mb-6">
                            Your Stripe account is fully set up and ready to receive payments.
                        </p>
                        <Button
                            onClick={() => navigate('/shoveller/searchJobByList')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                        >
                            <FaSearch className="w-4 h-4 mr-2" />
                            Look For Job Postings
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="flex flex-col px-6 py-10 max-w-4xl mx-auto">
                    <div className="text-center py-8">
                        <FaExclamationTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Stripe Setup Incomplete</h2>
                        <p className="text-gray-600 mb-6">
                            Your charges are not enabled yet. Please complete the setup to start receiving payments.
                        </p>
                        <Button
                            onClick={handleStripeSetup}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                        >
                            Complete Stripe Setup
                        </Button>
                    </div>
                </div>
            );
        }
    }

    // Failure State - Stripe setup failed
    if (status === 'failure') {
        const queryParams = new URLSearchParams(window.location.search);
        const errorDescription = queryParams.get('error_description');

        return (
            <div className="flex flex-col px-6 py-10 max-w-4xl mx-auto">
                <div className="text-center py-8">
                    <FaExclamationTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Stripe Setup Failed</h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        {errorDescription ? decodeURIComponent(errorDescription) : "Please try setting up your Stripe account again."}
                    </p>
                    <Button
                        onClick={handleStripeSetup}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                    >
                        Try Again With Stripe
                    </Button>
                </div>
            </div>
        );
    }

    if (loading) return <Loader />;

    // Main Stripe Onboarding UI
    return (
        <div className="flex flex-col px-6 py-10 max-w-4xl mx-auto">
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900">Payment Setup</h2>
            <p className="text-gray-600 mb-8">
                Connect your Stripe account to securely receive payments
            </p>

            <div className="space-y-6">
                {/* Information Card */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                        <FaInfoCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-blue-900 mb-2">
                                You need a Stripe account to securely get paid!
                            </h3>
                            <p className="text-blue-800 text-sm">
                                <span className="font-medium">Don't have a Stripe account?</span> Don't worry. 
                                Sign up with Stripe to securely receive payments directly to your personal bank account.
                            </p>
                            <button
                                onClick={handleStripeLoginNavigate}
                                className="flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800 font-semibold text-sm"
                            >
                                Sign up for Stripe <FaExternalLinkAlt className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Business Information Section */}
                <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                        <FaBuilding className="w-5 h-5" />
                        Business Information for Stripe
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                        Use this information when signing up for Stripe:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <BusinessInfoItem label="Business Location" content={businessInfo.location} />
                        <BusinessInfoItem label="Type of Business" content={businessInfo.type} />
                        <BusinessInfoItem label="Job Title" content={businessInfo.jobTitle} />
                        <BusinessInfoItem label="Industry" content={businessInfo.industries} />
                        <BusinessInfoItem 
                            label="Your Website" 
                            content={
                                <a
                                    href={`http://${businessInfo.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                >
                                    {businessInfo.website}
                                </a>
                            }
                        />
                        <BusinessInfoItem label="Product Description" content={businessInfo.productDescription} />
                        <BusinessInfoItem label="Statement Descriptor" content={businessInfo.statementDescriptor} />
                        <BusinessInfoItem label="Shortened Descriptor" content={businessInfo.shortenedDescriptor} />
                        <BusinessInfoItem 
                            label="Primary Product Type" 
                            content={businessInfo.primaryProductType} 
                        />
                    </div>
                </div>

                {/* Stripe Connect Section */}
                <div className="text-center pt-6 border-t">
                    <p className="text-gray-600 mb-4 text-sm">
                        Already have a Stripe Account?
                    </p>
                    <Button
                        onClick={handleStripeSetup}
                        disabled={loading}
                        className="w-full max-w-md bg-black hover:bg-gray-800 text-white py-3 text-lg font-semibold"
                    >
                        <FaCreditCard className="w-5 h-5 mr-2" />
                        {loading ? "Redirecting..." : "Connect Account With Stripe"}
                    </Button>
                </div>
            </div>
        </div>
    );
}